import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../constants/config';
import { apiFetchWithTokenRefresh } from '../utils/tokenInterceptor';
import { Alert } from 'react-native';

/** Authenticated fetch for admin APIs - handles both Bearer token and httpOnly cookie auth */
const adminFetch = async (url: string, options: RequestInit = {}) => {
  return apiFetchWithTokenRefresh(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(options.headers as Record<string, string>),
    },
  });
};

// Types
interface PaginationParams {
  page: number;
  limit: number;
}

interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface AdminStats {
  users: {
    total: number;
    active: number;
    inactive: number;
    admins: number;
    recent: number;
  };
  subscriptions: {
    free: number;
    pro: number;
    trial: number;
    pay_per_scan: number;
  };
  moodboards: {
    total: number;
    public: number;
    private: number;
    total_likes: number;
    total_views: number;
    recent: number;
  };
  feedback: {
    total: number;
    new: number;
    read: number;
    resolved: number;
    recent: number;
  };
}

// Query keys
export const adminQueryKeys = {
  stats: ['admin', 'stats'] as const,
  users: (params: PaginationParams) => ['admin', 'users', params] as const,
  subscriptions: (params: PaginationParams) =>
    ['admin', 'subscriptions', params] as const,
  moodboards: (params: PaginationParams) =>
    ['admin', 'moodboards', params] as const,
  feedback: (params: PaginationParams) =>
    ['admin', 'feedback', params] as const,
  auditLogs: (params: PaginationParams) =>
    ['admin', 'audit-logs', params] as const,
  userDetails: (userId: number) => ['admin', 'user', userId] as const,
  rolePermissions: ['admin', 'permissions', 'roles'] as const,
  subscriptionPermissions: ['admin', 'permissions', 'subscriptions'] as const,
  invites: (params: PaginationParams) => ['admin', 'invites', params] as const,
  featureFlags: (params: PaginationParams) =>
    ['admin', 'feature-flags', params] as const,
  featureFlagDetails: (flagId: number) =>
    ['admin', 'feature-flag', flagId] as const,
};

// Admin Stats Hook
export const useAdminStats = () => {
  return useQuery<AdminStats>({
    queryKey: adminQueryKeys.stats,
    queryFn: async () => {
      const response = await adminFetch(`${API_URL}/admin/stats`);
      if (!response.ok) {
        throw new Error('Failed to fetch admin stats');
      }
      return response.json();
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Users Hook
export const useUsers = (
  params: PaginationParams,
  options?: { enabled?: boolean }
) => {
  return useQuery<PaginatedResponse<Record<string, unknown>>>({
    queryKey: adminQueryKeys.users(params),
    queryFn: async () => {
      const skip = (params.page - 1) * params.limit;
      const response = await adminFetch(
        `${API_URL}/admin/users?skip=${skip}&limit=${params.limit}`
      );
      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ detail: 'Unknown error' }));
        const errorMessage =
          errorData.detail || `Failed to fetch users (${response.status})`;
        console.error('Admin API Error:', errorMessage);
        throw new Error(errorMessage);
      }
      return response.json();
    },
    enabled: options?.enabled !== false,
  });
};

// Subscriptions Hook
export const useSubscriptions = (
  params: PaginationParams,
  options?: { enabled?: boolean }
) => {
  return useQuery<PaginatedResponse<Record<string, unknown>>>({
    queryKey: adminQueryKeys.subscriptions(params),
    queryFn: async () => {
      const skip = (params.page - 1) * params.limit;
      const response = await adminFetch(
        `${API_URL}/admin/subscriptions?skip=${skip}&limit=${params.limit}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch subscriptions');
      }
      return response.json();
    },
    enabled: options?.enabled !== false,
  });
};

// Moodboards Hook
export const useMoodboards = (
  params: PaginationParams,
  filters?: {
    moderation_status?: string;
    user_id?: number;
    sort_by?: string;
    sort_order?: string;
    date_from?: string;
    date_to?: string;
  },
  options?: { enabled?: boolean }
) => {
  return useQuery<PaginatedResponse<Record<string, unknown>>>({
    queryKey: [...adminQueryKeys.moodboards(params), filters],
    queryFn: async () => {
      const skip = (params.page - 1) * params.limit;
      const queryParams = new URLSearchParams({
        skip: skip.toString(),
        limit: params.limit.toString(),
      });

      if (filters?.moderation_status) {
        queryParams.append('moderation_status', filters.moderation_status);
      }
      if (filters?.user_id) {
        queryParams.append('user_id', filters.user_id.toString());
      }
      if (filters?.sort_by) {
        queryParams.append('sort_by', filters.sort_by);
      }
      if (filters?.sort_order) {
        queryParams.append('sort_order', filters.sort_order);
      }
      if (filters?.date_from) {
        queryParams.append('date_from', filters.date_from);
      }
      if (filters?.date_to) {
        queryParams.append('date_to', filters.date_to);
      }

      const response = await adminFetch(
        `${API_URL}/admin/moodboards?${queryParams.toString()}`
      );
      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || 'Failed to fetch moodboards');
      }
      return response.json();
    },
    enabled: options?.enabled !== false,
  });
};

// Flagged Moodboards Hook
export const useFlaggedMoodboards = (
  params: PaginationParams,
  options?: { enabled?: boolean }
) => {
  return useQuery<PaginatedResponse<Record<string, unknown>>>({
    queryKey: ['admin', 'moodboards', 'flagged', params],
    queryFn: async () => {
      const skip = (params.page - 1) * params.limit;
      const response = await adminFetch(
        `${API_URL}/admin/moodboards/flagged?skip=${skip}&limit=${params.limit}`
      );
      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ detail: 'Unknown error' }));
        throw new Error(
          errorData.detail || 'Failed to fetch flagged moodboards'
        );
      }
      return response.json();
    },
    enabled: options?.enabled !== false,
  });
};

// Analytics Hook
export const useAnalytics = (
  period: string = 'month',
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: ['admin', 'analytics', period],
    queryFn: async () => {
      const response = await adminFetch(
        `${API_URL}/admin/analytics?period=${period}`
      );
      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || 'Failed to fetch analytics');
      }
      return response.json();
    },
    enabled: options?.enabled !== false,
  });
};

// Export CSV function
export const exportAnalyticsCSV = async (
  dataType: 'users' | 'moodboards' | 'palettes' | 'feedback'
) => {
  const response = await adminFetch(
    `${API_URL}/admin/analytics/export?data_type=${dataType}`
  );

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ detail: 'Unknown error' }));
    throw new Error(errorData.detail || 'Failed to export data');
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download =
    response.headers.get('Content-Disposition')?.split('filename=')[1] ||
    `${dataType}_export.csv`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

// Support Tickets Hook
export const useSupportTickets = (
  params: PaginationParams,
  filters?: { status?: string; priority?: string },
  options?: { enabled?: boolean }
) => {
  return useQuery<PaginatedResponse<Record<string, unknown>>>({
    queryKey: ['admin', 'support', 'tickets', params, filters],
    queryFn: async () => {
      const skip = (params.page - 1) * params.limit;
      const queryParams = new URLSearchParams({
        skip: skip.toString(),
        limit: params.limit.toString(),
      });

      if (filters?.status) {
        queryParams.append('status_filter', filters.status);
      }
      if (filters?.priority) {
        queryParams.append('priority_filter', filters.priority);
      }

      const response = await adminFetch(
        `${API_URL}/admin/support/tickets?${queryParams.toString()}`
      );
      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || 'Failed to fetch support tickets');
      }
      return response.json();
    },
    enabled: options?.enabled !== false,
  });
};

// Respond to ticket mutation
export const useRespondToTicket = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      ticketId,
      response,
    }: {
      ticketId: number;
      response: string;
    }) => {
      const response_data = await adminFetch(
        `${API_URL}/admin/support/tickets/${ticketId}/respond?response_text=${encodeURIComponent(response)}`,
        { method: 'POST' }
      );
      if (!response_data.ok) {
        const error = await response_data.json();
        throw new Error(error.detail || 'Failed to respond to ticket');
      }
      return response_data.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'support', 'tickets'],
      });
      Alert.alert('Success', 'Response sent successfully');
    },
    onError: (error: Error) => {
      Alert.alert('Error', error.message);
    },
  });
};

// Update ticket status mutation
export const useUpdateTicketStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      ticketId,
      status,
    }: {
      ticketId: number;
      status: string;
    }) => {
      const response = await adminFetch(
        `${API_URL}/admin/support/tickets/${ticketId}/status?new_status=${status}`,
        { method: 'PATCH' }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update ticket status');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'support', 'tickets'],
      });
      Alert.alert('Success', 'Ticket status updated');
    },
    onError: (error: Error) => {
      Alert.alert('Error', error.message);
    },
  });
};

// Announcements Hook
export const useAnnouncements = (
  activeOnly: boolean = false,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: ['admin', 'announcements', activeOnly],
    queryFn: async () => {
      const response = await adminFetch(
        `${API_URL}/admin/announcements?active_only=${activeOnly}`
      );
      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || 'Failed to fetch announcements');
      }
      return response.json();
    },
    enabled: options?.enabled !== false,
  });
};

// Create announcement mutation
export const useCreateAnnouncement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      title: string;
      message: string;
      type?: string;
      target_audience?: string;
      is_active?: boolean;
      starts_at?: string;
      ends_at?: string;
      call_to_action_text?: string;
      call_to_action_url?: string;
      image_url?: string;
    }) => {
      const params = new URLSearchParams({
        title: data.title,
        message: data.message,
        announcement_type: data.type || 'info',
        target_audience: data.target_audience || 'all',
        is_active: String(data.is_active !== false),
      });
      if (data.starts_at) params.append('starts_at', data.starts_at);
      if (data.ends_at) params.append('ends_at', data.ends_at);
      if (data.call_to_action_text)
        params.append('call_to_action_text', data.call_to_action_text);
      if (data.call_to_action_url)
        params.append('call_to_action_url', data.call_to_action_url);
      if (data.image_url) params.append('image_url', data.image_url);

      const response = await adminFetch(
        `${API_URL}/admin/announcements?${params.toString()}`,
        { method: 'POST' }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create announcement');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'announcements'] });
      Alert.alert('Success', 'Announcement created successfully');
    },
    onError: (error: Error) => {
      Alert.alert('Error', error.message);
    },
  });
};

// FAQs Hook
export const useFAQs = (
  activeOnly: boolean = false,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: ['admin', 'faqs', activeOnly],
    queryFn: async () => {
      const response = await adminFetch(
        `${API_URL}/admin/faqs?active_only=${activeOnly}`
      );
      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || 'Failed to fetch FAQs');
      }
      return response.json();
    },
    enabled: options?.enabled !== false,
  });
};

// Create FAQ mutation
export const useCreateFAQ = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      question: string;
      answer: string;
      category?: string;
      order?: number;
      is_active?: boolean;
    }) => {
      const params = new URLSearchParams({
        question: data.question,
        answer: data.answer,
        order: String(data.order || 0),
        is_active: String(data.is_active !== false),
      });
      if (data.category) params.append('category', data.category);

      const response = await adminFetch(
        `${API_URL}/admin/faqs?${params.toString()}`,
        { method: 'POST' }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create FAQ');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'faqs'] });
      Alert.alert('Success', 'FAQ created successfully');
    },
    onError: (error: Error) => {
      Alert.alert('Error', error.message);
    },
  });
};

// Update FAQ mutation
export const useUpdateFAQ = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      faqId,
      data,
    }: {
      faqId: number;
      data: Partial<{
        question: string;
        answer: string;
        category: string;
        order: number;
        is_active: boolean;
      }>;
    }) => {
      const params = new URLSearchParams();
      if (data.question !== undefined) params.append('question', data.question);
      if (data.answer !== undefined) params.append('answer', data.answer);
      if (data.category !== undefined) params.append('category', data.category);
      if (data.order !== undefined) params.append('order', String(data.order));
      if (data.is_active !== undefined)
        params.append('is_active', String(data.is_active));

      const response = await adminFetch(
        `${API_URL}/admin/faqs/${faqId}?${params.toString()}`,
        { method: 'PUT' }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update FAQ');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'faqs'] });
      Alert.alert('Success', 'FAQ updated successfully');
    },
    onError: (error: Error) => {
      Alert.alert('Error', error.message);
    },
  });
};

// Delete FAQ mutation
export const useDeleteFAQ = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (faqId: number) => {
      const response = await adminFetch(`${API_URL}/admin/faqs/${faqId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete FAQ');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'faqs'] });
      Alert.alert('Success', 'FAQ deleted successfully');
    },
    onError: (error: Error) => {
      Alert.alert('Error', error.message);
    },
  });
};

// Feedback Hook
export const useFeedback = (
  params: PaginationParams,
  options?: { enabled?: boolean }
) => {
  return useQuery<PaginatedResponse<Record<string, unknown>>>({
    queryKey: adminQueryKeys.feedback(params),
    queryFn: async () => {
      const skip = (params.page - 1) * params.limit;
      const response = await adminFetch(
        `${API_URL}/admin/feedback?skip=${skip}&limit=${params.limit}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch feedback');
      }
      return response.json();
    },
    enabled: options?.enabled !== false,
  });
};

// Update Feedback Status Hook
export const useUpdateFeedbackStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      feedbackId,
      status,
    }: {
      feedbackId: number;
      status: string;
    }) => {
      const response = await adminFetch(
        `${API_URL}/admin/feedback/${feedbackId}/status?new_status=${status}`,
        { method: 'PATCH' }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update feedback status');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'feedback'] });
      Alert.alert('Success', 'Feedback status updated');
    },
    onError: (error: Error) => {
      Alert.alert('Error', error.message);
    },
  });
};

// Audit Logs Hook
export const useAuditLogs = (
  params: PaginationParams,
  filters?: {
    action?: string;
    action_type?: string;
    target_type?: string;
    admin_user_id?: number;
  },
  options?: { enabled?: boolean }
) => {
  return useQuery<PaginatedResponse<Record<string, unknown>>>({
    queryKey: [...adminQueryKeys.auditLogs(params), filters],
    queryFn: async () => {
      const skip = (params.page - 1) * params.limit;
      const queryParams = new URLSearchParams({
        skip: skip.toString(),
        limit: params.limit.toString(),
      });

      if (filters?.action) {
        queryParams.append('action_filter', filters.action);
      }
      if (filters?.action_type) {
        queryParams.append('action_type_filter', filters.action_type);
      }
      if (filters?.target_type) {
        queryParams.append('target_type_filter', filters.target_type);
      }
      if (filters?.admin_user_id) {
        queryParams.append('admin_user_id', filters.admin_user_id.toString());
      }

      const response = await adminFetch(
        `${API_URL}/admin/audit-logs?${queryParams.toString()}`
      );
      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || 'Failed to fetch audit logs');
      }
      return response.json();
    },
    enabled: options?.enabled !== false,
  });
};

// Invites Hook
export const useInvites = (
  params: PaginationParams,
  filters?: {
    inviter_id?: number;
    invitee_id?: number;
    used?: boolean;
  },
  options?: { enabled?: boolean }
) => {
  return useQuery<{ items: Record<string, unknown>[]; total: number }>({
    queryKey: [...adminQueryKeys.invites(params), filters],
    queryFn: async () => {
      const skip = (params.page - 1) * params.limit;
      const queryParams = new URLSearchParams({
        skip: skip.toString(),
        limit: params.limit.toString(),
      });

      if (filters?.inviter_id) {
        queryParams.append('inviter_id', filters.inviter_id.toString());
      }
      if (filters?.invitee_id) {
        queryParams.append('invitee_id', filters.invitee_id.toString());
      }
      if (filters?.used !== undefined) {
        queryParams.append('used', filters.used.toString());
      }

      const response = await adminFetch(
        `${API_URL}/admin/invites?${queryParams.toString()}`
      );
      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || 'Failed to fetch invites');
      }
      return response.json();
    },
    enabled: options?.enabled !== false,
  });
};

// User Details Hook
export const useUserDetails = (userId: number | null) => {
  return useQuery({
    queryKey: adminQueryKeys.userDetails(userId || 0),
    queryFn: async () => {
      if (!userId) return null;
      const response = await adminFetch(`${API_URL}/admin/users/${userId}`);
      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ detail: 'Unknown error' }));
        const errorMessage =
          errorData.detail ||
          `Failed to fetch user details (${response.status})`;
        console.error('User details API Error:', errorMessage);
        throw new Error(errorMessage);
      }
      return response.json();
    },
    enabled: !!userId,
  });
};

// Mutation Hooks
export const useSuspendUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: number) => {
      const response = await adminFetch(
        `${API_URL}/admin/users/${userId}/suspend`,
        {
          method: 'POST',
        }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to suspend user');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
      Alert.alert('Success', 'User has been suspended');
    },
    onError: (error: Error) => {
      Alert.alert('Error', error.message);
    },
  });
};

export const useActivateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: number) => {
      const response = await adminFetch(
        `${API_URL}/admin/users/${userId}/activate`,
        { method: 'POST' }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to activate user');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
      Alert.alert('Success', 'User has been activated');
    },
    onError: (error: Error) => {
      Alert.alert('Error', error.message);
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: number) => {
      const response = await adminFetch(
        `${API_URL}/admin/users/${userId}/delete`,
        {
          method: 'POST',
        }
      );
      if (!response.ok) {
        let errorMessage = 'Failed to delete user';
        try {
          const error = await response.json();
          errorMessage = error.detail || error.message || errorMessage;
        } catch {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
      return response.json();
    },
    onSuccess: () => {
      // Invalidate all user-related queries
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'userDetails'] });
      Alert.alert('Success', 'User has been deleted');
    },
    onError: (error: Error) => {
      console.error('Delete user error:', error);
      Alert.alert('Error', error.message);
    },
  });
};

export const useUpdateUserRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, role }: { userId: number; role: string }) => {
      const response = await adminFetch(
        `${API_URL}/admin/users/${userId}/role?role=${role}`,
        { method: 'PUT' }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update user role');
      }
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({
        queryKey: adminQueryKeys.userDetails(variables.userId),
      });
      Alert.alert('Success', `User role updated to ${variables.role}`);
    },
    onError: (error: Error) => {
      Alert.alert('Error', error.message);
    },
  });
};

/** Update a user's subscription plan (admin only). For testing without Paddle. */
export const useUpdateUserSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      subscription_plan,
    }: {
      userId: number;
      subscription_plan: string;
    }) => {
      const response = await adminFetch(
        `${API_URL}/admin/users/${userId}/subscription`,
        {
          method: 'PUT',
          body: JSON.stringify({ subscription_plan }),
        }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update subscription');
      }
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({
        queryKey: adminQueryKeys.userDetails(variables.userId),
      });
      Alert.alert(
        'Success',
        `Subscription set to ${variables.subscription_plan}. Refresh the app to see changes.`
      );
    },
    onError: (error: Error) => {
      Alert.alert('Error', error.message);
    },
  });
};

export const useHideMoodboard = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (moodboardId: number) => {
      const response = await adminFetch(
        `${API_URL}/admin/moodboards/${moodboardId}/hide`,
        { method: 'POST' }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to hide moodboard');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'moodboards'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
      Alert.alert('Success', 'Moodboard has been hidden');
    },
    onError: (error: Error) => {
      Alert.alert('Error', error.message);
    },
  });
};

export const useShowMoodboard = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (moodboardId: number) => {
      const response = await adminFetch(
        `${API_URL}/admin/moodboards/${moodboardId}/show`,
        { method: 'POST' }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to show moodboard');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'moodboards'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
      Alert.alert('Success', 'Moodboard has been shown');
    },
    onError: (error: Error) => {
      Alert.alert('Error', error.message);
    },
  });
};

export const useApproveMoodboard = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (moodboardId: number) => {
      const response = await adminFetch(
        `${API_URL}/admin/moodboards/${moodboardId}/approve`,
        { method: 'POST' }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to approve moodboard');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'moodboards'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
      Alert.alert('Success', 'Moodboard has been approved');
    },
    onError: (error: Error) => {
      Alert.alert('Error', error.message);
    },
  });
};

export const useRejectMoodboard = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (moodboardId: number) => {
      const response = await adminFetch(
        `${API_URL}/admin/moodboards/${moodboardId}/reject`,
        { method: 'POST' }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to reject moodboard');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'moodboards'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
      Alert.alert('Success', 'Moodboard has been rejected');
    },
    onError: (error: Error) => {
      Alert.alert('Error', error.message);
    },
  });
};

export const useDeleteMoodboard = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (moodboardId: number) => {
      const response = await adminFetch(
        `${API_URL}/admin/moodboards/${moodboardId}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete moodboard');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'moodboards'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
      Alert.alert('Success', 'Moodboard has been deleted');
    },
    onError: (error: Error) => {
      Alert.alert('Error', error.message);
    },
  });
};

export const useFlagMoodboard = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (moodboardId: number) => {
      const response = await adminFetch(
        `${API_URL}/admin/moodboards/${moodboardId}/flag`,
        { method: 'POST' }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to flag moodboard');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'moodboards'] });
      queryClient.invalidateQueries({
        queryKey: ['admin', 'moodboards', 'flagged'],
      });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
      Alert.alert('Success', 'Moodboard has been flagged');
    },
    onError: (error: Error) => {
      Alert.alert('Error', error.message);
    },
  });
};

// Flagged content moderation hooks
export const useRejectFlaggedMoodboard = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      moodboardId,
      reason,
    }: {
      moodboardId: number;
      reason: string;
    }) => {
      const response = await adminFetch(
        `${API_URL}/admin/moodboards/${moodboardId}/flagged/reject`,
        {
          method: 'POST',
          body: JSON.stringify({
            confirmed: true,
            reason: reason.trim(),
          }),
        }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to reject flagged moodboard');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'moodboards'] });
      queryClient.invalidateQueries({
        queryKey: ['admin', 'moodboards', 'flagged'],
      });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
      Alert.alert('Success', 'Flagged request has been rejected');
    },
    onError: (error: Error) => {
      Alert.alert('Error', error.message);
    },
  });
};

export const useApproveFlaggedMoodboard = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      moodboardId,
      reason,
    }: {
      moodboardId: number;
      reason: string;
    }) => {
      const response = await adminFetch(
        `${API_URL}/admin/moodboards/${moodboardId}/flagged/approve`,
        {
          method: 'POST',
          body: JSON.stringify({
            confirmed: true,
            reason: reason.trim(),
          }),
        }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to approve flagged moodboard');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'moodboards'] });
      queryClient.invalidateQueries({
        queryKey: ['admin', 'moodboards', 'flagged'],
      });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
      Alert.alert('Success', 'Flagged moodboard has been approved and hidden');
    },
    onError: (error: Error) => {
      Alert.alert('Error', error.message);
    },
  });
};

export const useDeleteFlaggedMoodboard = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      moodboardId,
      reason,
    }: {
      moodboardId: number;
      reason?: string;
    }) => {
      const response = await adminFetch(
        `${API_URL}/admin/moodboards/${moodboardId}/flagged/delete`,
        {
          method: 'POST',
          body: JSON.stringify({
            confirmed: true,
            reason: reason?.trim() || undefined,
          }),
        }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete flagged moodboard');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'moodboards'] });
      queryClient.invalidateQueries({
        queryKey: ['admin', 'moodboards', 'flagged'],
      });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
      Alert.alert('Success', 'Moodboard has been permanently deleted');
    },
    onError: (error: Error) => {
      Alert.alert('Error', error.message);
    },
  });
};

// Permissions Hooks
export const useRolePermissions = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: adminQueryKeys.rolePermissions,
    queryFn: async () => {
      const response = await adminFetch(`${API_URL}/admin/permissions/roles`);
      if (!response.ok) {
        let errorMessage = `Failed to fetch role permissions (${response.status})`;
        try {
          const errorData = await response.json();
          // Handle validation errors (422) which return an array of errors
          if (Array.isArray(errorData.detail)) {
            errorMessage = errorData.detail
              .map(
                (err: { loc?: string[]; msg?: string; message?: string }) =>
                  `${err.loc?.join('.') || 'field'}: ${err.msg || err.message || 'validation error'}`
              )
              .join(', ');
          } else {
            errorMessage =
              errorData.detail || errorData.message || errorMessage;
          }
        } catch {
          // If JSON parsing fails, use status text
          errorMessage = response.statusText || errorMessage;
        }
        const error = new Error(errorMessage) as Error & {
          status?: number;
          response?: Response;
        };
        error.status = response.status;
        error.response = response;
        throw error;
      }
      return response.json();
    },
    enabled: options?.enabled !== false,
  });
};

export const useSubscriptionPermissions = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: adminQueryKeys.subscriptionPermissions,
    queryFn: async () => {
      const response = await adminFetch(
        `${API_URL}/admin/permissions/subscriptions`
      );
      if (!response.ok) {
        let errorMessage = `Failed to fetch subscription permissions (${response.status})`;
        try {
          const errorData = await response.json();
          // Handle validation errors (422) which return an array of errors
          if (Array.isArray(errorData.detail)) {
            errorMessage = errorData.detail
              .map(
                (err: { loc?: string[]; msg?: string; message?: string }) =>
                  `${err.loc?.join('.') || 'field'}: ${err.msg || err.message || 'validation error'}`
              )
              .join(', ');
          } else {
            errorMessage =
              errorData.detail || errorData.message || errorMessage;
          }
        } catch {
          // If JSON parsing fails, use status text
          errorMessage = response.statusText || errorMessage;
        }
        const error = new Error(errorMessage) as Error & {
          status?: number;
          response?: Response;
        };
        error.status = response.status;
        error.response = response;
        throw error;
      }
      return response.json();
    },
    enabled: options?.enabled !== false,
  });
};

export const useInitializePermissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await adminFetch(
        `${API_URL}/admin/permissions/initialize`,
        {
          method: 'POST',
        }
      );
      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || 'Failed to initialize permissions');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminQueryKeys.rolePermissions,
      });
      queryClient.invalidateQueries({
        queryKey: adminQueryKeys.subscriptionPermissions,
      });
      Alert.alert('Success', 'Permissions initialized successfully');
    },
    onError: (error: Error) => {
      Alert.alert('Error', error.message);
    },
  });
};

// Feature Flags Hooks
export const useFeatureFlags = (
  params: PaginationParams,
  options?: { enabled?: boolean }
) => {
  return useQuery<PaginatedResponse<Record<string, unknown>>>({
    queryKey: adminQueryKeys.featureFlags(params),
    queryFn: async () => {
      const skip = (params.page - 1) * params.limit;
      const response = await adminFetch(
        `${API_URL}/admin/feature-flags?skip=${skip}&limit=${params.limit}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch feature flags');
      }
      return response.json();
    },
    enabled: options?.enabled !== false,
  });
};

export const useFeatureFlagDetails = (
  flagId: number,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: adminQueryKeys.featureFlagDetails(flagId),
    queryFn: async () => {
      const response = await adminFetch(
        `${API_URL}/admin/feature-flags/${flagId}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch feature flag details');
      }
      return response.json();
    },
    enabled: options?.enabled !== false && !!flagId,
  });
};

export const useCreateFeatureFlag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      name: string;
      description?: string;
      is_enabled_globally?: boolean;
      enabled_for_roles?: string[];
    }) => {
      const response = await adminFetch(`${API_URL}/admin/feature-flags`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || 'Failed to create feature flag');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
      Alert.alert('Success', 'Feature flag created successfully');
    },
    onError: (error: Error) => {
      Alert.alert('Error', error.message);
    },
  });
};

export const useUpdateFeatureFlag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      flagId,
      data,
    }: {
      flagId: number;
      data: {
        description?: string;
        is_enabled_globally?: boolean;
        enabled_for_roles?: string[];
      };
    }) => {
      const response = await adminFetch(
        `${API_URL}/admin/feature-flags/${flagId}`,
        {
          method: 'PUT',
          body: JSON.stringify(data),
        }
      );
      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || 'Failed to update feature flag');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
      Alert.alert('Success', 'Feature flag updated successfully');
    },
    onError: (error: Error) => {
      Alert.alert('Error', error.message);
    },
  });
};

export const useDeleteFeatureFlag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (flagId: number) => {
      const response = await adminFetch(
        `${API_URL}/admin/feature-flags/${flagId}`,
        {
          method: 'DELETE',
        }
      );
      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || 'Failed to delete feature flag');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
      Alert.alert('Success', 'Feature flag deleted successfully');
    },
    onError: (error: Error) => {
      Alert.alert('Error', error.message);
    },
  });
};

export const useAssignFeatureFlagToUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      flagId,
      userId,
      isEnabled,
    }: {
      flagId: number;
      userId: number;
      isEnabled: boolean;
    }) => {
      const response = await adminFetch(
        `${API_URL}/admin/feature-flags/${flagId}/users`,
        {
          method: 'POST',
          body: JSON.stringify({ user_id: userId, is_enabled: isEnabled }),
        }
      );
      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ detail: 'Unknown error' }));
        throw new Error(
          errorData.detail || 'Failed to assign feature flag to user'
        );
      }
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: adminQueryKeys.featureFlagDetails(variables.flagId),
      });
      queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
      Alert.alert('Success', 'Feature flag assignment updated successfully');
    },
    onError: (error: Error) => {
      Alert.alert('Error', error.message);
    },
  });
};

export const useRemoveFeatureFlagFromUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      flagId,
      userId,
    }: {
      flagId: number;
      userId: number;
    }) => {
      const response = await adminFetch(
        `${API_URL}/admin/feature-flags/${flagId}/users/${userId}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ detail: 'Unknown error' }));
        throw new Error(
          errorData.detail || 'Failed to remove feature flag from user'
        );
      }
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: adminQueryKeys.featureFlagDetails(variables.flagId),
      });
      queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
      Alert.alert('Success', 'Feature flag assignment removed successfully');
    },
    onError: (error: Error) => {
      Alert.alert('Error', error.message);
    },
  });
};
